import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  companyIdSelector,
  DashboardApi,
  dashboardDtoAtom,
  dashboardErrorsAtom,
  errors,
  IApiError,
  IDashboardDataRequest,
  isLoggedInAtom,
  Log,
  normalizeErrors,
} from "@ryde/shared";

export const useDashboardActions = () => {
  const { t } = useTranslation();
  const isLoggedIn = useRecoilValue(isLoggedInAtom);
  const setDashboardData = useSetRecoilState(dashboardDtoAtom);
  const setDashboardError = useSetRecoilState(dashboardErrorsAtom);
  const setDashboardIsFetching = useSetRecoilState(apiDataFetchingAtom);
  const companyId = useRecoilValue(companyIdSelector);

  /** Callers name the cards they render — see `IDashboardDataRequest`. */
  const fetchDashboardData = useEvent(
    async (query: IDashboardDataRequest): Promise<boolean> => {
      if (!isLoggedIn || !companyId) {
        return false;
      }

      Log.debug("fetching dashboard data");

      try {
        setDashboardIsFetching(true);
        const dto = await DashboardApi.getDashboard(query);

        if (!dto) {
          setDashboardError([t(errors.common.emptyApiResponse)]);
          return false;
        }

        setDashboardData(dto);
        return true;
      } catch (error) {
        Log.errorAndToast(t("errors.failedFetchDashboard"), error);
        setDashboardError(normalizeErrors(error as IApiError, t));
        return false;
      } finally {
        setDashboardIsFetching(false);
      }
    },
  );
  return {
    fetchDashboardData,
  };
};
